<!--
	@name app-source-result-dialog-near-misses
	@description Near Miss dialog
	@date 2026/07/31
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<common-dialog ref="dialog" :component="$options.name" :persistent="true" @closed="revert">
		<template #header> Near Misses </template>
		<template #body>
			<v-sheet>
				<p class="text-body-2 mb-4">
					Some suitable options may fall just outside your criteria. Near Misses show products that are close to meeting your selected criteria requirements.
					<v-icon size="22" color="primary" class="mr-1"> mdi-video-box </v-icon>
					<a class="font-weight-bold" @click="openTutorial">Watch the tutorial guide to learn more</a>. Got a great idea for the next Near Miss option you'd like to see?
					<v-icon size="22" color="primary" class="mr-1"> mdi-comment-quote </v-icon>
					<a class="font-weight-bold ga--trigger" @click="submitFeedback">Click here to submit your suggestion</a>.
				</p>

				<v-select
					v-if="availableOptions.length"
					v-model="selectedRefs"
					:items="availableOptions"
					item-text="name"
					item-value="ref"
					placeholder="Choice a Near Miss option"
					multiple
					outlined
					hide-details
				>
					<template #selection="{ index }">
						<span v-if="index === 0"> {{ selectedOptions.length }} Near Miss{{ selectedOptions.length !== 1 ? 'es' : '' }} applied </span>
					</template>
				</v-select>

				<v-divider v-if="selectedOptions.length || !availableOptions.length" inset class="mt-5" />

				<v-alert type="warning" v-if="!availableOptions.length" class="mt-6 mb-0">
					<p class="text-body-2 mb-0">
						There are no Near Miss options available for your current search criteria. Near Miss options are currently available for
						<span class="font-weight-bold">Initial Advance</span> and <span class="font-weight-bold">Maximum Monthly Repayments</span>.
					</p>
				</v-alert>

				<div v-else-if="selectedOptions.length" class="text-center mt-5 mb-2">
					<p v-if="impactLoading" class="text-body-2 font-weight-medium mb-1">Recalculating...</p>
					<p v-else-if="impact" class="text-body-2 font-weight-medium mb-1">
						With the current Near Miss options applied, an
						<span class="font-weight-black">
							additional {{ impact.combined.additionalProductCount }} product{{ impact.combined.additionalProductCount !== 1 ? 's' : '' }}
						</span>
						will be available
					</p>
					<div v-if="!impactLoading && impact" class="text-caption plain grey--text text--darken-2">
						<v-icon color="grey darken-2" size="14" class="mr-1"> mdi-brain </v-icon>Analytics powered by
						<span class="ai--text font-weight-medium">Air Insights</span>
					</div>
				</div>

				<validation-observer v-slot="{ invalid }" ref="observer">
					<v-form @submit.prevent="submit">
						<v-sheet v-for="item in selectedOptions" :key="item.ref" class="grey lighten-3 rounded-sm pt-2 pb-4 px-4 mt-5">
							<span class="teal lighten-4 rounded-sm px-3 py-1 teal--text text--darken-4 text-caption plain" style="position: relative; left: -24px">
								{{ item.name }}
							</span>
							<v-row no-gutters class="mt-2">
								<v-col class="grow">
									<p class="text-caption plain mb-0" v-html="$sanitize(formatDescription(item))" />
								</v-col>
								<v-col class="shrink pl-6">
									<validation-provider v-slot="{ errors }" name="Condition" :rules="item.rules">
										<v-text-field
											v-model="values[item.ref]"
											:error-messages="errors"
											solo
											dense
											:prefix="item.method === 'amount' ? '£' : null"
											:append-icon="item.method === 'amount' ? null : 'mdi-percent-outline'"
											hide-details
											style="width: 120px"
											@input="update"
										/>
									</validation-provider>
								</v-col>
							</v-row>
						</v-sheet>

						<div v-if="selectedOptions.length" class="d-flex justify-center mt-5">
							<v-btn :disabled="invalid" color="primary" type="submit"> apply Near Misses </v-btn>
						</div>

						<div v-if="!selectedOptions.length && filters.length" class="d-flex justify-center mt-5">
							<v-btn class="mr-4" color="error" type="submit"> confirm remove active Near Misses </v-btn>
						</div>
					</v-form>
				</validation-observer>
			</v-sheet>
		</template>
	</common-dialog>
</template>

<script>
	import { mapActions } from 'vuex';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';
	import CommonDialog from '@/component/common/dialog';
	import { cloneDeep, debounce } from 'lodash';
	import numeral from 'numeral';
	import { EventBus, GoogleAnalytics, evaluateConditions } from '@/utils';

	export default {
		name: 'app-source-result-dialog-near-misses',

		components: { CommonDialog, ValidationObserver, ValidationProvider },

		props: {
			filterModel: { type: Object, default: () => ({}) },
			processId: { type: String, default: null },
			iterationId: { type: String, default: null }
		},

		data() {
			return {
				options: [],
				filters: [], // Snapshot of what was applied when the dialog opened
				selectedRefs: [],
				values: {}, // { [ref]: '10' } - local only, never written back into the cms catalogue
				impact: null,
				impactLoading: false,
				impactSequence: 0
			};
		},

		computed: {
			/**
			 * @name availableOptions
			 * @description Options whose cms conditions pass against the current filters, e.g. an initial advance
			 * Near Miss is meaningless when the adviser asked for the maximum available
			 */
			availableOptions() {
				return this.options.filter((option) => evaluateConditions(option.conditions, this.filterModel));
			},

			selectedOptions() {
				const refs = new Set(this.selectedRefs);
				return this.availableOptions.filter((option) => refs.has(option.ref));
			},

			/**
			 * @name nearMisses
			 * @description The object the back end consumes. ref is the filter key the tolerance widens
			 */
			nearMisses() {
				return this.selectedOptions.map((option) => ({
					ref: option.ref,
					method: option.method || 'percentage',
					value: Number(this.values[option.ref])
				}));
			}
		},

		watch: {
			nearMisses: {
				deep: true,
				handler() {
					this.queueImpact();
				}
			}
		},

		created() {
			// The percentage field is free text, so wait for a pause in typing before asking the back end
			this.queueImpact = debounce(this.fetchImpact, 400);
		},

		methods: {
			...mapActions('LifetimeMortgageSourcing', ['fetchNearMissImpact']),

			/**
			 * @name open
			 * @description Open the dialog against the applied Near Misses and the cms form item that declares the catalogue
			 * @param {Array} nearMissModel the currently applied Near Misses
			 * @param {Object} item the near-misses cms form item
			 */
			open(nearMissModel, item = {}) {
				this.options = item.options || [];
				this.filters = cloneDeep(nearMissModel || []);
				this.selectedRefs = this.filters.map(({ ref }) => ref);
				this.values = this.buildValues();
				this.impact = null;

				GoogleAnalytics.nearMissesOpened();
				this.$refs.dialog.open();
				this.queueImpact();
			},

			/**
			 * @name buildValues
			 * @description Seed each option's percentage from what is applied, falling back to the cms default
			 * @returns {Object} Values keyed by ref
			 */
			buildValues() {
				return this.options.reduce((values, option) => {
					const applied = this.filters.find(({ ref }) => ref === option.ref);
					values[option.ref] = String(applied ? applied.value : this.defaultFor(option));
					return values;
				}, {});
			},

			/**
			 * @name defaultFor
			 * @description Seed an option's tolerance. An amount is seeded as a percentage of the adviser's own figure,
			 * so a £20,000 advance defaults to £2,000 - it is then absolute and does not rescale if they change the figure.
			 * @param {Object} option the cms option
			 * @returns {Number} Seed value
			 */
			defaultFor(option) {
				if (option.method !== 'amount' || !option.defaultPercentageOfSource) return option.defaultValue;

				const sourceValue = Number(this.filterModel[option.ref]) || 0;
				return Math.round((sourceValue * option.defaultPercentageOfSource) / 100);
			},

			/**
			 * @name toleranceOf
			 * @description The tolerance an option currently represents, in the same units as the adviser's figure
			 * @param {Object} item the option
			 * @returns {Number} Tolerance
			 */
			toleranceOf(item) {
				const value = Number(this.values[item.ref]) || 0;
				if (item.method === 'amount') return value;

				return ((Number(this.filterModel[item.ref]) || 0) * value) / 100;
			},

			/**
			 * @name revert
			 * @description Restore the selection and percentages that were applied when the dialog opened.
			 * Bound to the dialog's close event so dismissing without submitting discards in-progress edits.
			 */
			revert() {
				this.selectedRefs = this.filters.map(({ ref }) => ref);
				this.values = this.buildValues();
			},

			/**
			 * @name formatDescription
			 * @description Build the cms description sentence from the adviser's live filter value.
			 * direction is display only - it decides which way [CALCULATED] moves
			 * @param {Object} item the option
			 * @returns {String} Description
			 */
			formatDescription(item) {
				const sourceValue = Number(this.filterModel[item.ref]) || 0;
				const tolerance = this.toleranceOf(item);
				const calculated = item.direction === 'increase' ? sourceValue + tolerance : Math.max(0, sourceValue - tolerance);

				// An amount reads as money, a percentage reads as the raw number the adviser typed
				const value = item.method === 'amount' ? numeral(tolerance).format('0,0') : Number(this.values[item.ref]) || 0;

				return item.description
					.replace('[SOURCE]', numeral(sourceValue).format('0,0'))
					.replace('[VALUE]', value)
					.replace('[CALCULATED]', numeral(calculated).format('0,0'));
			},

			/**
			 * @debounce @name update
			 * @description Re-validate the form after a tolerance field changes. The actual preview fetch is
			 * driven separately by the nearMisses watcher, debounced via queueImpact.
			 */
			update: debounce(async function () {
				await this.$refs.observer.validate();
			}, 500),

			/**
			 * @name breakdownFor
			 * @description How many extra products this criterion brings in on its own. Per criterion figures do not
			 * sum to the combined figure, as a product blocked by two criteria needs both tolerances applied
			 * @param {String} ref the Near Miss ref
			 * @returns {Number|null} Additional product count, null while unknown
			 */
			breakdownFor(ref) {
				return this.impact?.breakdown?.find((entry) => entry.ref === ref)?.additionalProductCount ?? null;
			},

			/**
			 * @name fetchImpact
			 * @description Preview the effect of the current tolerances. Decoration only - a failure must never
			 * block the adviser from applying their Near Misses
			 */
			async fetchImpact() {
				if (!this.processId || !this.iterationId || !this.selectedOptions.length) {
					this.impact = null;
					this.impactLoading = false;
					return;
				}

				// Don't round trip a half typed value.
				const inRange = this.nearMisses.every(({ method, value }) => {
					if (!Number.isInteger(value)) return false;
					if (method === 'amount') return value >= 1;
					return value >= 1 && value <= 100;
				});
				if (!inRange) return;

				const sequence = ++this.impactSequence;
				this.impactLoading = true;

				try {
					const { data } = await this.fetchNearMissImpact({
						processId: this.processId,
						iterationId: this.iterationId,
						// sortConfig is required by the shared new iteration schema. Ordering is irrelevant to a count, so send an empty one
						data: { sortConfig: [], filters: { ...this.filterModel, nearMisses: this.nearMisses } }
					});

					if (sequence !== this.impactSequence) return; // A newer request has already been fired
					this.impact = data;
				} catch (error) {
					if (sequence !== this.impactSequence) return;
					this.impact = null;
				} finally {
					if (sequence === this.impactSequence) this.impactLoading = false;
				}
			},

			async submit() {
				const isValid = await this.$refs.observer.validate();
				if (!isValid) return;

				const nearMisses = this.nearMisses;
				this.$emit('submit', nearMisses);

				GoogleAnalytics.nearMissesSubmitted({
					near_miss_name: this.selectedOptions.map(({ name }) => name).toString()
				});

				this.$refs.dialog.close();
			},

            openTutorial() {
                EventBus.$emit('open-on-air-article', 'e860dc8e-4b9a-49ae-b0ef-b0f682347ae0');
            },

			submitFeedback() {
				if (
					window._iub?.cs?.consent?.purposes &&
					(Array.isArray(window._iub.cs.consent.purposes) || typeof window._iub.cs.consent.purposes === 'object') &&
					window._iub.cs.consent.purposes[5]
				) {
					GoogleAnalytics.nearMissSurveyOpened();
				} else {
					EventBus.$emit('open-chat');
				}
			}
		}
	};
</script>
